import type {
  PostSessionDTO,
  PostSessionParticipantsDTO,
  LbCallSession,
  GetSessionsInput,
  SessionsResult,
} from './callSession.types';
import { Platform } from './callSession.types';
import { Strategy, type BusinessServiceConfig } from '../core';
import { AbstractBusinessService } from '../core/abstractBusinessService';
import { sessionApi } from '../session';
import {
  type CallHistoryOffsetConnection,
  type CallHistorySessionForCsv,
  SessionCallType,
  SessionPlatformInput,
} from '../graphQlTypes.generated';
import { getIsProvider } from '../../features/realtime/realtime.selectors';
import { CallType } from '../../shared/types';

const strategyMap = {
  postSession: Strategy.OG,
  postSessionParticipants: Strategy.OG,
  postSessionEnd: Strategy.OG,
  getSessions: Strategy.OG,
  getSessionsForCsv: Strategy.OG,
};

/**
 * GraphQL CreateSession only exposes web/mobile; map local platforms for API compatibility.
 */
const toSessionPlatform = (platform: Platform): SessionPlatformInput => {
  if (platform === Platform.Mobile || platform === Platform.Phone) {
    return SessionPlatformInput.Mobile;
  }
  return SessionPlatformInput.Web;
};

const toSessionCallType = (callType: CallType): SessionCallType => {
  switch (callType) {
    case CallType.Audio:
      return SessionCallType.Voice;
    case CallType.Phone:
      return SessionCallType.Phone;
    case CallType.Video:
    default:
      return SessionCallType.Video;
  }
};

const toStringArray = (ids?: string[]): string[] =>
  ids?.map((id) => id.toString()) || [];

export type CallSessionServiceInitConfig<StoreType> = Omit<
  BusinessServiceConfig<StoreType>,
  'serviceName' | 'strategyMap'
>;

/**
 * This service refers to a "call session".
 *
 * We store some information about calls in our database.
 */
export class CallSessionService<StoreType> extends AbstractBusinessService<
  keyof typeof strategyMap,
  StoreType
> {
  constructor({ store, logger }: CallSessionServiceInitConfig<StoreType>) {
    super({ serviceName: 'CallSessionService', store, logger, strategyMap });
  }

  async postSession(
    roomId: string,
    payload: PostSessionDTO,
  ): Promise<LbCallSession> {
    if (this.getStrategy('postSession') === Strategy.OG) {
      const response = await this.dispatchAction<{
        createSession: LbCallSession;
      }>(
        sessionApi.endpoints.CreateSession.initiate({
          input: {
            roomId,
            channel: payload.channel,
            internal: payload.internal,
            isWeb: payload.isWeb,
            participantsCount: payload.participantsCount,
            pidList: payload.pidList,
            patientPlatform: toSessionPlatform(payload.patientPlatform),
            doctorPlatform: toSessionPlatform(payload.doctorPlatform),
            ...(payload.callType !== undefined && {
              callType: toSessionCallType(payload.callType),
            }),
          },
        }),
      );

      if ('data' in response) {
        return response.data.createSession;
      } else if ('errors' in response && Array.isArray(response.errors)) {
        throw response.errors[0];
      }
    }
    throw new Error('Strategy not supported');
  }

  postSessionParticipants(
    sessionId: string,
    payload: PostSessionParticipantsDTO,
  ): void {
    if (this.getStrategy('postSessionParticipants') === Strategy.OG) {
      void this.dispatchAction(
        sessionApi.endpoints.AddSessionParticipant.initiate({
          input: {
            sessionId,
            participantUuid: payload.participantUuid,
            waitTime: payload.waitTime,
          },
        }),
      );
    }
  }

  async postSessionEnd(
    dbSessionId: string,
    participantsCount?: number,
    roomId?: string,
  ): Promise<void> {
    if (this.getStrategy('postSessionEnd') === Strategy.OG) {
      const input = { sessionId: dbSessionId, participantsCount };
      const isProvider = getIsProvider(this.store.getState());

      if (isProvider) {
        await this.dispatchAction(
          sessionApi.endpoints.EndSessionFromProvider.initiate({ input }),
        );
      } else {
        await this.dispatchAction(
          sessionApi.endpoints.EndSessionFromPatient.initiate({
            input: { ...input, ...(roomId && { roomId }) },
          }),
        );
      }
    }
  }

  async getSessions(input: GetSessionsInput): Promise<SessionsResult> {
    if (this.getStrategy('getSessions') === Strategy.OG) {
      const response = await this.dispatchAction<{
        callHistory: CallHistoryOffsetConnection;
      }>(
        sessionApi.endpoints.CallHistory.initiate(
          {
            offset: input.offset,
            limit: input.limit,
            input: {
              start: input.start,
              end: input.end,
              roomIds: toStringArray(input.roomIds),
              userIds: toStringArray(input.userIds),
            },
          },
          {
            forceRefetch: true,
            subscribe: false,
          },
        ),
      );

      if ('data' in response) {
        const { callHistory } = response.data;

        return {
          callSessions: callHistory?.edges?.map(({ node }) => node),
          total: callHistory?.pageInfo?.totalCount ?? 0,
        };
      } else if ('errors' in response && Array.isArray(response.errors)) {
        throw response.errors[0];
      }
    }
    throw new Error('Strategy not supported');
  }

  async getSessionsForCsv(
    input: GetSessionsInput,
  ): Promise<CallHistorySessionForCsv[]> {
    if (this.getStrategy('getSessionsForCsv') === Strategy.OG) {
      const response = await this.dispatchAction<{
        callHistoryForCsv: CallHistorySessionForCsv[];
      }>(
        sessionApi.endpoints.CallHistoryForCsv.initiate(
          {
            input: {
              start: input.start,
              end: input.end,
              roomIds: toStringArray(input.roomIds),
              userIds: toStringArray(input.userIds),
            },
          },
          {
            forceRefetch: true,
            subscribe: false,
          },
        ),
      );

      if ('data' in response) {
        return response.data.callHistoryForCsv;
      } else if ('errors' in response && Array.isArray(response.errors)) {
        throw response.errors[0];
      }
    }
    throw new Error('Strategy not supported');
  }
}
